import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {EXPERIMENT_VALIDATION_CONSTANTS} from 'studies/StudyConstants';
import ReplicateLimitLabel from 'studies/typed/shared/ReplicateLimitLabel';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import ButtonGroup from 'ui-library/buttons/ButtonGroup';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import stopPropagation from 'util/stopPropagation';

import {Condition} from '../process-development/useProcessDevelopmentStudyState';
import AddPlannedConditionsModal from './AddPlannedConditionsModal';
import {getConditionCountsByName, getDefaultConditionNames} from './conditionUtils';
import {
  isAtPlannedConditionsLimit,
  MAX_PLAN_CONDITION_COUNT,
  MethodExecutionLimitState,
} from './useMethodExecutionLimitState';

export interface EditNumPlannedConditionsButtonsProps {
  conditions: Array<Condition>;
  setConditions: (conditions: Array<Condition>, prevIndexes: Array<number | null>) => void;
  methodExecutionLimitState: MethodExecutionLimitState;
  procedureType: ProcedureType;
}

export function EditNumPlannedConditionsButtons(props: EditNumPlannedConditionsButtonsProps): JSX.Element {
  const isAtLimit = isAtPlannedConditionsLimit(props.methodExecutionLimitState);

  function addConditions(numConditions: number, numReplicates: number): void {
    // We get these for all the conditions even though we only need default names for the new conditions,
    // since we want the index included in the name to take into account existing conditions
    const defaultConditionsNames = getDefaultConditionNames(
      props.conditions.length + numConditions,
      props.procedureType
    );
    const existingConditionNames = props.conditions.map((condition) => condition.name);
    const newConditionNames = defaultConditionsNames.slice(existingConditionNames.length);
    const allConditionNames = existingConditionNames.concat(newConditionNames);
    const conditionCounts = getConditionCountsByName(allConditionNames);

    props.setConditions(
      [
        // existing conditions
        ...props.conditions.map((condition) => ({
          ...condition,
          validationState: {
            ...condition.validationState,
            isDuplicateName: conditionCounts.get(condition.name)! > 1,
            isNameLengthOverLimit:
              condition.name.length > EXPERIMENT_VALIDATION_CONSTANTS.CONDITION_NAME_MAX_LENGTH,
            isDescLengthOverLimit:
              (condition.description ?? '').length >
              EXPERIMENT_VALIDATION_CONSTANTS.CONDITION_DESCRIPTION_MAX_LENGTH,
          },
        })),
        // new conditions
        ...Array.from({length: numConditions}, (_, index: number) => ({
          numReplicates,
          name: newConditionNames[index],
          validationState: {
            isDuplicateName: conditionCounts.get(newConditionNames[index])! > 1,
            isNameLengthOverLimit:
              newConditionNames[index].length > EXPERIMENT_VALIDATION_CONSTANTS.CONDITION_NAME_MAX_LENGTH,
            isDescLengthOverLimit: false,
          },
        })),
      ],
      [...props.conditions.map((_, i) => i), ...new Array(numConditions).fill(null)]
    );
  }

  // New conditions are added with the same number of replicates as the last condition by default.
  const lastNumReplicates =
    props.conditions.length > 0 ? props.conditions[props.conditions.length - 1].numReplicates : 1;

  return (
    <div className={css(styles.buttonContainer)}>
      <div
        className={css(styles.button)}
        // These controls will be embedded in a table header. Double-clicking on the table header will
        // toggle the table between the collapsed and expanded states. If the user clicks on any of these
        // controls in quick succession, a double-click event will bubble up to the table and cause it to
        // toggle its collapsed state. To prevent this, we prevent the propagation of the double-click event.
        onDoubleClick={stopPropagation}
      >
        <Tooltip
          text={`Cannot exceed ${MAX_PLAN_CONDITION_COUNT} planned conditions`}
          isDisabled={!isAtLimit}
        >
          <ButtonGroup>
            <Button
              buttonStyle={ButtonStyle.TERTIARY_BLUE}
              icon="compose"
              size={ButtonSize.MINI}
              onClick={() => {
                addConditions(1, lastNumReplicates);
              }}
              disabled={isAtLimit}
              id="add-condition-button"
            />
            <Dropdown
              menu={
                <Menu width={MenuWidth.SMALL}>
                  <Option
                    dataTestAttrs={{
                      component: 'AddConditionsDropdown',
                      element: 'addMultipleConditionsButton',
                    }}
                    onSelect={() => {
                      legacyRunModal(AddPlannedConditionsModal, {
                        onRequestSubmit: ({numConditions, numReplicates}) => {
                          addConditions(numConditions, numReplicates);
                        },
                        remainingConditions: MAX_PLAN_CONDITION_COUNT - props.conditions.length,
                        procedureType: props.procedureType,
                      });
                    }}
                  >
                    {`Add multiple ${getProcedureRunConditionTextForProcedureType(props.procedureType, {
                      plural: true,
                    })}`}
                  </Option>
                </Menu>
              }
            >
              <Button
                buttonStyle={ButtonStyle.TERTIARY_BLUE}
                size={ButtonSize.MINI}
                isDropdown={true}
                hideDropdownCaret={true}
                icon="chevron-down"
                iconAltText="Show dropdown menu"
                dataTestAttrs={{component: 'AddConditionsDropdown', element: 'addMultipleConditionsDropdown'}}
                disabled={isAtLimit}
                id="add-multiple-conditions-dropdown-button"
              />
            </Dropdown>
          </ButtonGroup>
        </Tooltip>
      </div>
      <ReplicateLimitLabel replicateLimit={props.methodExecutionLimitState.replicateLimit} />
    </div>
  );
}

const styles = StyleSheet.create({
  button: {paddingTop: '3px'},
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: '12px',
    marginLeft: 12,
  },
});
